import axios from "axios";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const getCookieValue = (cookieName) => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cookieName}=`));
  if (!match) {
    return null;
  }
  const value = match.substring(cookieName.length + 1);
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

// Request interceptor to add CSRF token
apiClient.interceptors.request.use(
  async (config) => {
    // For state-changing methods, ensure CSRF cookie is set
    if (["post", "put", "patch", "delete"].includes(config.method)) {
      // Get CSRF cookie if not already set
      await getCsrfCookie();
    }

    const csrfToken = getCookieValue("XSRF-TOKEN");

    if (csrfToken) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers["X-XSRF-TOKEN"] = csrfToken;
    }

    // Add share_token to query params if present in URL
    const urlParams = new URLSearchParams(window.location.search);
    const shareToken = urlParams.get("share_token");
    if (shareToken) {
      if (!config.params) {
        config.params = {};
      }
      config.params.share_token = shareToken;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareToken = urlParams.get("share_token");

    if (
      error.response?.status === 401 &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/signup" &&
      !shareToken
    ) {
      // Only redirect to login if not already on login/signup page and no share token
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// Get CSRF cookie
export const getCsrfCookie = async () => {
  await axios.get(`${API_BASE_URL}/sanctum/csrf-cookie`, {
    withCredentials: true,
  });
};

// Auth APIs
export const signup = async (data) => {
  const response = await apiClient.post("/api/v1/signup", data);
  return response.data;
};

export const login = async (data) => {
  const response = await apiClient.post("/api/v1/login", data);
  return response.data;
};

export const logout = async () => {
  const response = await apiClient.post("/api/v1/logout");
  return response.data;
};

export const getUser = async () => {
  const response = await apiClient.get("/api/v1/user");
  return response.data;
};

// Project APIs
export const getProjects = async () => {
  const response = await apiClient.get("/api/v1/project");
  return response.data;
};

export const createProject = async (data) => {
  const response = await apiClient.post("/api/v1/project", data);
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await apiClient.get(`/api/v1/project/${projectId}`);
  return response.data;
};

export const updateProject = async (projectId, data) => {
  const response = await apiClient.put(`/api/v1/project/${projectId}`, data);
  return response.data;
};

export const deleteProject = async (projectId) => {
  const response = await apiClient.delete(`/api/v1/project/${projectId}`);
  return response.data;
};

// Category APIs
export const getCategories = async (projectId) => {
  const response = await apiClient.get(`/api/v1/project/${projectId}/category`);
  return response.data;
};

export const createCategory = async (projectId, data) => {
  const response = await apiClient.post(
    `/api/v1/project/${projectId}/category`,
    data,
  );
  return response.data;
};

export const updateCategory = async (projectId, categoryId, data) => {
  const response = await apiClient.put(
    `/api/v1/project/${projectId}/category/${categoryId}`,
    data,
  );
  return response.data;
};

export const deleteCategory = async (projectId, categoryId) => {
  const response = await apiClient.delete(
    `/api/v1/project/${projectId}/category/${categoryId}`,
  );
  return response.data;
};

// Question APIs
export const getQuestions = async (projectId) => {
  const response = await apiClient.get(`/api/v1/project/${projectId}/question`);
  return response.data;
};

export const createQuestion = async (projectId, data) => {
  const response = await apiClient.post(
    `/api/v1/project/${projectId}/question`,
    data,
  );
  return response.data;
};

export const bulkCreateQuestions = async (projectId, data) => {
  const response = await apiClient.post(
    `/api/v1/project/${projectId}/question/bulk`,
    data,
  );
  return response.data;
};

export const updateQuestion = async (projectId, questionId, data) => {
  const response = await apiClient.put(
    `/api/v1/project/${projectId}/question/${questionId}`,
    data,
  );
  return response.data;
};

export const deleteQuestion = async (projectId, questionId) => {
  const response = await apiClient.delete(
    `/api/v1/project/${projectId}/question/${questionId}`,
  );
  return response.data;
};

// Comment APIs
export const getComments = async (projectId, questionId) => {
  const response = await apiClient.get(
    `/api/v1/project/${projectId}/question/${questionId}/comment`,
  );
  return response.data;
};

export const createComment = async (projectId, questionId, data) => {
  const response = await apiClient.post(
    `/api/v1/project/${projectId}/question/${questionId}/comment`,
    data,
  );
  return response.data;
};

export const updateComment = async (projectId, questionId, commentId, data) => {
  const response = await apiClient.patch(
    `/api/v1/project/${projectId}/question/${questionId}/comment/${commentId}`,
    data,
  );
  return response.data;
};

export const deleteComment = async (projectId, questionId, commentId) => {
  const response = await apiClient.delete(
    `/api/v1/project/${projectId}/question/${questionId}/comment/${commentId}`,
  );
  return response.data;
};

// Share APIs
export const getShareInfo = async (projectId) => {
  const response = await apiClient.get(
    `/api/v1/project/${projectId}/share/info`,
  );
  return response.data;
};

export const getShares = async (projectId) => {
  const response = await apiClient.get(`/api/v1/project/${projectId}/share`);
  return response.data;
};

export const createShare = async (projectId, data) => {
  const response = await apiClient.post(
    `/api/v1/project/${projectId}/share`,
    data,
  );
  return response.data;
};

// Notification APIs
export const getNotifications = async () => {
  const response = await apiClient.get("/api/v1/notification");
  return response.data;
};

export const markNotificationRead = async (notificationUserId) => {
  const response = await apiClient.put(
    `/api/v1/notification/${notificationUserId}`,
  );
  return response.data;
};

export default apiClient;
